import React, { useState, useEffect } from 'react';
import { View, Modal, TouchableOpacity, StyleSheet, FlatList, TextInput, ActivityIndicator, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import ThemedText from '../common/ThemedText';
import * as networkApi from '../../lib/api/network';
import { storage } from '../../utils/storage';

interface User {
  id: string;
  name: string | null;
  designation?: string | null;
  company?: string | null;
  businessName?: string | null;
  photo?: string | null;
  businessPhoto?: string | null;
}

interface AddMemberModalProps {
  visible: boolean;
  onClose: () => void;
  onAddMembers: (userIds: string[]) => void;
  existingMemberIds: string[]; // IDs of users already in the ledger
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  visible,
  onClose,
  onAddMembers,
  existingMemberIds,
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!visible) {
      // Reset state when modal closes
      setSearchQuery('');
      setSelectedUserIds([]);
      setUsers([]);
    }
  }, [visible]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const timer = setTimeout(() => {
        searchUsers();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      // Clear users when search is empty
      setUsers([]);
    }
  }, [searchQuery]);

  const searchUsers = async () => {
    const token = await storage.getToken();
    if (!token) return;

    try {
      setSearching(true);
      const response = await networkApi.searchUsers(token, searchQuery, 1, 20);
      
      // Filter out current user and existing members
      const filteredUsers = response.data.filter(
        (u: any) => u.id !== user?.userId && !existingMemberIds.includes(u.id)
      );
      
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setSearching(false);
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAddMembers = () => {
    console.log('🔘 handleAddMembers called, selectedUserIds:', selectedUserIds);
    if (selectedUserIds.length > 0) {
      console.log('➕ Adding members:', {
        count: selectedUserIds.length,
        userIds: selectedUserIds,
        users: users.filter(u => selectedUserIds.includes(u.id)).map(u => ({ id: u.id, name: u.name }))
      });
      onAddMembers(selectedUserIds);
      onClose();
    } else {
      console.log('❌ No users selected');
    }
  };

  const renderUser = ({ item }: { item: User }) => {
    const isSelected = selectedUserIds.includes(item.id);

    return (
      <TouchableOpacity
        style={[
          styles.userItem,
          {
            backgroundColor: theme.card,
            borderColor: isSelected ? theme.accent : theme.border,
            borderWidth: isSelected ? 2 : 1,
          },
        ]}
        onPress={() => toggleUserSelection(item.id)}
        activeOpacity={0.7}
      >
        <View style={styles.userInfo}>
          <View style={[styles.avatar, { backgroundColor: theme.accent + '20' }]}>
            {item.photo || item.businessPhoto ? (
              <Image
                source={{ uri: item.photo || item.businessPhoto || '' }}
                style={styles.avatarImage}
              />
            ) : (
              <ThemedText style={[styles.avatarText, { color: theme.accent }]}>
                {(item.name || '?').charAt(0).toUpperCase()}
              </ThemedText>
            )}
          </View>
          <View style={styles.userDetails}>
            <ThemedText style={[styles.userName, { color: theme.text }]} numberOfLines={1}>
              {item.name || 'Unknown'}
            </ThemedText>
            {(item.designation || item.businessName) && (
              <ThemedText
                style={[styles.userMeta, { color: theme.textSecondary }]}
                numberOfLines={1}
              >
                {item.designation && item.businessName
                  ? `${item.designation} at ${item.businessName}`
                  : item.designation || item.businessName}
              </ThemedText>
            )}
          </View>
        </View>
        <View
          style={[
            styles.checkbox,
            {
              backgroundColor: isSelected ? theme.accent : 'transparent',
              borderColor: isSelected ? theme.accent : theme.border,
            },
          ]}
        >
          {isSelected && <Ionicons name="checkmark" size={16} color="#FFFFFF" />}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={false}
      onRequestClose={onClose}
    >
      <View style={[styles.modalContainer, { backgroundColor: theme.background }]}>
        {/* Header */}
        <View style={[styles.header, { borderBottomColor: theme.border, paddingTop: insets.top }]}>
          <TouchableOpacity onPress={onClose} style={styles.backButton} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Ionicons name="arrow-back" size={24} color={theme.text} />
          </TouchableOpacity>
          <ThemedText style={[styles.title, { color: theme.text }]}>Add Members</ThemedText>
          <View style={{ width: 40 }} />
        </View>

        {/* Search */}
        <View style={styles.searchContainer}>
            <View style={[styles.searchBox, { backgroundColor: theme.backgroundTertiary, borderColor: theme.border }]}>
              <Ionicons name="search" size={20} color={theme.textSecondary} />
              <TextInput
                style={[styles.searchInput, { color: theme.text }]}
                placeholder="Search users..."
                placeholderTextColor={theme.textTertiary}
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
              {searching && <ActivityIndicator size="small" color={theme.accent} />}
            </View>
          </View>

        {/* Selected Count */}
        {selectedUserIds.length > 0 && (
          <View style={styles.selectedCountContainer}>
              <ThemedText style={[styles.selectedCount, { color: theme.accent }]}>
                {selectedUserIds.length} member{selectedUserIds.length > 1 ? 's' : ''} selected
              </ThemedText>
            </View>
          )}

        {/* User List */}
        <FlatList
            data={users}
            renderItem={renderUser}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
            ListEmptyComponent={
              loading || searching ? (
                <ActivityIndicator size="large" color={theme.accent} style={{ marginTop: 40 }} />
              ) : (
                <View style={styles.emptyState}>
                  <Ionicons name="search-outline" size={48} color={theme.textTertiary} />
                  <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
                    {searchQuery ? 'No users found' : 'Search for users to add'}
                  </ThemedText>
                  {!searchQuery && (
                    <ThemedText style={[styles.emptySubtext, { color: theme.textTertiary }]}>
                      Start typing to find users
                    </ThemedText>
                  )}
                </View>
              )
            }
          />

        {/* Footer */}
        <View style={[styles.footer, { borderTopColor: theme.border, paddingBottom: (insets.bottom || 0) + 16 }]}>
            <TouchableOpacity
              style={[styles.cancelButton, { borderColor: theme.border }]}
              onPress={onClose}
              activeOpacity={0.7}
            >
              <ThemedText style={[styles.cancelButtonText, { color: theme.textSecondary }]}>
                Cancel
              </ThemedText>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.addButton,
                {
                  backgroundColor: selectedUserIds.length > 0 ? theme.accent : theme.border,
                },
              ]}
              onPress={handleAddMembers}
              disabled={selectedUserIds.length === 0}
              activeOpacity={0.7}
            >
              <ThemedText style={[styles.addButtonText, { color: '#FFFFFF' }]}>
                Add {selectedUserIds.length > 0 ? `(${selectedUserIds.length})` : ''}
              </ThemedText>
            </TouchableOpacity>
          </View>
        </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
  },
  searchContainer: {
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 8,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
  },
  selectedCountContainer: {
    paddingHorizontal: 24,
    paddingBottom: 8,
  },
  selectedCount: {
    fontSize: 14,
    fontWeight: '600',
  },
  listContent: {
    paddingHorizontal: 24,
    paddingBottom: 16,
  },
  userItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 12,
    marginBottom: 8,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 40,
    height: 40,
  },
  avatarText: {
    fontSize: 18,
    fontWeight: '700',
  },
  userDetails: {
    flex: 1,
  },
  userName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  userMeta: {
    fontSize: 13,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 16,
    marginTop: 12,
  },
  emptySubtext: {
    fontSize: 14,
    marginTop: 4,
  },
  footer: {
    flexDirection: 'row',
    paddingHorizontal: 24,
    paddingTop: 16,
    borderTopWidth: 1,
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  addButton: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default AddMemberModal;
